import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Textarea } from "@/components/ui/textarea";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { X, Save, Calendar, Loader2 } from "lucide-react";
import { mockDataStore, mockIntegrations } from "../utils/mockData";
import { generateFollowUpReminderEmail } from "./EmailTemplates";
import { addMonths, format } from "date-fns";

export default function FollowUpScheduler({
  patient,
  onClose,
  onSchedule,
  doctorName,
}) {
  const [followUpPeriod, setFollowUpPeriod] = useState("3months");
  const [notes, setNotes] = useState(
    "Routine follow-up blood work based on recent analysis."
  );
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [lastTest, setLastTest] = useState(null);

  useEffect(() => {
    const fetchLastTest = async () => {
      const tests = await mockDataStore.filter(
        "bloodTests",
        { patient_id: patient.patient_id },
        "-test_date",
        1
      );
      if (tests.length > 0) {
        setLastTest(tests[0]);
      }
    };
    fetchLastTest();
  }, [patient.patient_id]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError(null);

    try {
      const monthsToAdd = followUpPeriod === "3months" ? 3 : 6;
      const followUpDate = addMonths(new Date(), monthsToAdd);

      const requestData = {
        patient_name: `${patient.first_name} ${patient.last_name}`,
        patient_dob: patient.date_of_birth,
        patient_address: `${patient.address.street}, ${patient.address.city}, ${patient.address.state} ${patient.address.zip_code}`,
        patient_phone: patient.phone,
        patient_email: patient.email,
        referring_doctor: doctorName || "Dr. Johnson",
        requested_tests: lastTest?.results
          ? Object.keys(lastTest.results)
          : ["Comprehensive Panel"],
        preferred_date: format(followUpDate, "yyyy-MM-dd"),
        preferred_time_slot: "morning",
        clinical_notes: notes,
        status: "follow_up",
      };

      await mockDataStore.create("conciergeRequests", requestData);

      const emailPayload = generateFollowUpReminderEmail({
        patientName: patient.first_name,
        followUpDate: format(followUpDate, "MMMM d, yyyy"),
        doctorName: doctorName || "Dr. Johnson",
        followUpPeriod: `${monthsToAdd} months`,
      });

      await mockIntegrations.SendEmail({
        to: patient.email,
        from_name: "ClubMed Concierge",
        subject: emailPayload.subject,
        body: emailPayload.html,
      });

      onSchedule();
    } catch (err) {
      setError("Failed to schedule follow-up. Please try again.");
      console.error(err);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <Card className="w-full max-w-lg bg-white/90 backdrop-blur-xl border-slate-200/60 shadow-2xl">
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="flex items-center gap-2">
            <Calendar className="w-5 h-5" />
            Schedule Follow-up
          </CardTitle>
          <Button variant="ghost" size="icon" onClick={onClose}>
            <X className="w-4 h-4" />
          </Button>
        </CardHeader>
        <CardContent className="p-6">
          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <Label>Patient</Label>
              <p className="font-semibold text-slate-800">
                {patient.first_name} {patient.last_name}
              </p>
            </div>

            <div className="space-y-2">
              <Label>Follow-up Period</Label>
              <RadioGroup
                value={followUpPeriod}
                onValueChange={setFollowUpPeriod}
              >
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="3months" id="3months" />
                  <Label htmlFor="3months">3 Months</Label>
                </div>
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="6months" id="6months" />
                  <Label htmlFor="6months">6 Months</Label>
                </div>
              </RadioGroup>
            </div>

            <div className="space-y-2">
              <Label htmlFor="notes">Notes for Follow-up Request</Label>
              <Textarea
                id="notes"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="e.g., Repeat lipid panel and check thyroid function..."
                rows={3}
              />
            </div>

            {error && (
              <Alert variant="destructive">
                <AlertTitle>Error</AlertTitle>
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}

            <div className="flex justify-end gap-3 pt-4">
              <Button type="button" variant="outline" onClick={onClose}>
                Cancel
              </Button>
              <Button type="submit" disabled={isLoading}>
                {isLoading ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Save className="w-4 h-4 mr-2" />
                )}
                Schedule
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
